import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Banknote, Wallet, Clock, CheckCircle2, ReceiptText, UploadCloud, X, Truck, Store } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const MODES = [
  { value: "cash", label: "CASH", icon: Banknote },
  { value: "gcash", label: "GCASH", icon: Wallet },
  { value: "maya", label: "MAYA", icon: Wallet },
  { value: "bank_transfer", label: "BANK TRANSFER", icon: ReceiptText },
  { value: "cash_on_pickup", label: "CASH ON PICKUP", icon: Store },
  { value: "cash_on_delivery", label: "CASH ON DELIVERY", icon: Truck },
  { value: "on_account", label: "ON ACCOUNT", icon: Clock },
];

const DP_MODES = [
  { value: "cash", label: "Cash" },
  { value: "gcash", label: "GCash" },
  { value: "maya", label: "Maya" },
  { value: "bank_transfer", label: "Bank transfer" },
];

const CashierDashboard = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [creditCustomers, setCreditCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Payment dialog
  const [target, setTarget] = useState(null);
  const [mode, setMode] = useState("cash");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(0);
  const [proofUrl, setProofUrl] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [creditCustomerId, setCreditCustomerId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paymentType, setPaymentType] = useState("full"); // full | down_payment
  const [dpMode, setDpMode] = useState("cash");
  const [dpAmount, setDpAmount] = useState(0);
  const proofInput = useRef(null);

  const fetchAll = async () => {
    try {
      const [o, p, c] = await Promise.all([
        api.get("/cashier/orders"),
        api.get("/cashier/payments"),
        api.get("/customers/credit-enabled"),
      ]);
      setData(o.data); setPayments(p.data); setCreditCustomers(c.data);
    } catch { toast.error("Failed to load cashier data"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [o, p, c] = await Promise.all([
          api.get("/cashier/orders"),
          api.get("/cashier/payments"),
          api.get("/customers/credit-enabled"),
        ]);
        if (!cancelled) { setData(o.data); setPayments(p.data); setCreditCustomers(c.data); }
      } catch {
        if (!cancelled) toast.error("Failed to load cashier data");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-open payment modal if ?order=X param present (deep-link from production board)
  useEffect(() => {
    const orderId = searchParams.get("order");
    if (!orderId || !data) return;
    const order = data.pending.find((o) => o.order_id === orderId);
    if (order) openPayment(order);
    // eslint-disable-next-line
  }, [data, searchParams]);

  const openPayment = (order) => {
    setTarget(order);
    setMode("cash"); setReferenceNumber(""); setProofUrl(null);
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setAmount(order.total); setCreditCustomerId("");
    setPaymentType("full"); setDpMode("cash"); setDpAmount(0);
  };

  const uploadProof = async (file) => {
    if (!file) return;
    setUploadingProof(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofUrl(data.file_url);
    } catch { toast.error("Upload failed"); }
    finally { setUploadingProof(false); }
  };

  const submit = async () => {
    if (!target) return;
    setSubmitting(true);
    try {
      const payload = {
        order_id: target.order_id,
        mode,
        amount: parseFloat(amount) || target.total,
        reference_number: referenceNumber || null,
        payment_date: paymentDate || null,
        proof_url: proofUrl,
        on_account_customer_id: mode === "on_account" ? creditCustomerId : null,
        payment_type: paymentType,
        down_payment_mode: paymentType === "down_payment" ? dpMode : null,
        down_payment_amount: paymentType === "down_payment" ? parseFloat(dpAmount) || 0 : 0,
      };
      await api.post("/cashier/process-payment", payload);
      const msg = mode === "on_account" ? "Booked on account (UNPAID)"
        : paymentType === "down_payment" ? "Down payment received — production starts"
        : "Payment received";
      toast.success(msg);
      setTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  if (loading || !data) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;

  const stats = {
    pending: data.pending.length,
    paid_today: data.paid_today.length,
    on_account: data.on_account.length,
    paid_today_sum: data.paid_today.reduce((a, b) => a + (b.total || 0), 0),
    on_account_sum: data.on_account.reduce((a, b) => a + (b.total || 0), 0),
  };

  const needsReference = ["gcash", "maya", "bank_transfer"].includes(mode);
  const isCOPD = ["cash_on_pickup", "cash_on_delivery"].includes(mode);
  const dpNeedsProof = isCOPD && paymentType === "down_payment" && dpMode !== "cash";

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="cashier-dashboard">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Cashier</p>
        <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Payments desk</h1>
        <p className="text-sm text-neutral-500 mt-1">Confirm payments and book on-account transactions.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="pav-card" data-testid="stat-pending">
          <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Pending payment</p>
          <p className="font-display text-3xl font-extrabold text-[#06B6D4] mt-2">{stats.pending}</p>
        </div>
        <div className="pav-card" data-testid="stat-paid-today">
          <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Paid today</p>
          <p className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-2">{stats.paid_today}</p>
          <p className="text-xs text-neutral-500 mt-1">{peso(stats.paid_today_sum)}</p>
        </div>
        <div className="pav-card" data-testid="stat-on-account">
          <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">On account (unpaid)</p>
          <p className="font-display text-3xl font-extrabold text-red-600 mt-2">{stats.on_account}</p>
          <p className="text-xs text-neutral-500 mt-1">{peso(stats.on_account_sum)}</p>
        </div>
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="bg-[#F4F6FB] mb-4">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({stats.pending})</TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid">Paid today ({stats.paid_today})</TabsTrigger>
          <TabsTrigger value="on_account" data-testid="tab-on-account">On account ({stats.on_account})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">All payments</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <OrderTable orders={data.pending} onProcess={openPayment} actionLabel="Process payment" />
        </TabsContent>
        <TabsContent value="paid">
          <OrderTable orders={data.paid_today} showPayment />
        </TabsContent>
        <TabsContent value="on_account">
          <OrderTable orders={data.on_account} showPayment unpaidFlag />
        </TabsContent>
        <TabsContent value="payments">
          <div className="pav-card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Txn</th>
                    <th className="text-left px-3 py-2">Order</th>
                    <th className="text-left px-3 py-2">Mode</th>
                    <th className="text-left px-3 py-2">Reference</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {payments.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-neutral-500">No payments yet.</td></tr>}
                  {payments.map((p) => (
                    <tr key={p.transaction_id} data-testid={`payment-row-${p.transaction_id}`}>
                      <td className="px-3 py-2 text-neutral-600">{new Date(p.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono text-xs">{p.transaction_id}</td>
                      <td className="px-3 py-2 font-semibold">{p.order_id}</td>
                      <td className="px-3 py-2 capitalize">{(p.mode || p.method || "").replace(/_/g, " ")}</td>
                      <td className="px-3 py-2 text-neutral-500">{p.reference_number || "—"}</td>
                      <td className="px-3 py-2 text-right font-bold">{peso(p.amount)}</td>
                      <td className="px-3 py-2">
                        <span className={`pav-chip ${p.payment_status === "unpaid" ? "status-cancelled" : p.payment_status === "partial" ? "status-pending_payment" : "status-completed"}`}>
                          {p.payment_status || "paid"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Process payment — {target?.order_id}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#F4F6FB] rounded-lg p-3 text-sm">
              <div className="flex justify-between"><span>Customer</span><span className="font-semibold">{target?.customer_name}</span></div>
              <div className="flex justify-between mt-1"><span>Total</span><span className="font-display font-extrabold text-[#0B1E3F]">{peso(target?.total)}</span></div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-2">Mode of payment</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {MODES.map((m) => (
                  <button key={m.value} type="button" onClick={() => { setMode(m.value); if (!["cash_on_pickup","cash_on_delivery"].includes(m.value)) setPaymentType("full"); }}
                    data-testid={`payment-mode-${m.value}`}
                    className={`p-2 rounded-lg border-2 text-[10px] uppercase tracking-wider font-bold transition ${mode === m.value ? "border-[#0B1E3F] bg-[#0B1E3F] text-white" : "border-[#E2E8F0] bg-white hover:border-[#0B1E3F]/40 text-neutral-700"}`}>
                    <m.icon className="h-4 w-4 mx-auto mb-1" />
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* COP/COD: Full or Down Payment */}
            {isCOPD && (
              <div className="border-l-4 border-blue-500 pl-3 space-y-3" data-testid="copd-section">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-2">Payment type</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setPaymentType("full")}
                      data-testid="payment-type-full"
                      className={`flex-1 p-2 rounded-lg border-2 text-xs font-bold transition ${paymentType === "full" ? "border-[#0B1E3F] bg-[#0B1E3F] text-white" : "border-[#E2E8F0] bg-white"}`}>
                      Full Payment ({mode === "cash_on_pickup" ? "on pickup" : "on delivery"})
                    </button>
                    <button type="button" onClick={() => setPaymentType("down_payment")}
                      data-testid="payment-type-dp"
                      className={`flex-1 p-2 rounded-lg border-2 text-xs font-bold transition ${paymentType === "down_payment" ? "border-[#06B6D4] bg-[#06B6D4] text-white" : "border-[#E2E8F0] bg-white"}`}>
                      Down Payment
                    </button>
                  </div>
                </div>
                {paymentType === "down_payment" && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">DP mode</label>
                        <Select value={dpMode} onValueChange={setDpMode}>
                          <SelectTrigger className="pav-input mt-1" data-testid="dp-mode-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DP_MODES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">DP amount (₱)</label>
                        <input type="number" min="0" step="0.01" className="pav-input mt-1"
                          data-testid="dp-amount-input"
                          value={dpAmount} onChange={(e) => setDpAmount(e.target.value)} />
                      </div>
                    </div>
                    {dpNeedsProof && (
                      <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Proof / screenshot (required for non-cash DP)</label>
                        <ProofUploader proofUrl={proofUrl} setProofUrl={setProofUrl}
                          uploadingProof={uploadingProof} uploadProof={uploadProof} inputRef={proofInput} />
                      </div>
                    )}
                  </>
                )}
                <p className="text-xs text-blue-700">
                  {paymentType === "down_payment"
                    ? "Down payment locked in — production starts. Remaining balance collected on " + (mode === "cash_on_pickup" ? "pickup." : "delivery.")
                    : "Marked as Payment Pending. Cashier collects on " + (mode === "cash_on_pickup" ? "pickup." : "delivery.") + " Production starts now."}
                </p>
              </div>
            )}

            {!isCOPD && (
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Amount</label>
                <input type="number" min="0" step="0.01" className="pav-input mt-1"
                  data-testid="payment-amount-input"
                  value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
            )}

            {needsReference && (
              <div className="space-y-3 border-l-4 border-[#06B6D4] pl-3" data-testid="reference-section">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Reference number</label>
                  <input className="pav-input mt-1" data-testid="payment-reference-input"
                    value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Date of transaction</label>
                  <input type="date" className="pav-input mt-1" data-testid="payment-date-input"
                    value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Screenshot / proof</label>
                  <ProofUploader proofUrl={proofUrl} setProofUrl={setProofUrl}
                    uploadingProof={uploadingProof} uploadProof={uploadProof} inputRef={proofInput} />
                </div>
              </div>
            )}

            {mode === "on_account" && (
              <div className="border-l-4 border-red-500 pl-3" data-testid="on-account-section">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Bill to credit-eligible customer</label>
                <Select value={creditCustomerId} onValueChange={setCreditCustomerId}>
                  <SelectTrigger className="pav-input mt-1" data-testid="on-account-customer-select"><SelectValue placeholder="Select customer with credit line" /></SelectTrigger>
                  <SelectContent>
                    {creditCustomers.length === 0 ? (
                      <SelectItem value="_none" disabled>No customers have credit enabled</SelectItem>
                    ) : creditCustomers.map((c) => (
                      <SelectItem key={c.user_id} value={c.user_id}>
                        {c.name} {c.company ? `(${c.company})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-red-600 mt-2">This transaction will be marked UNPAID and added to the customer&apos;s outstanding balance.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setTarget(null)} className="btn-outline text-sm">Cancel</button>
            <button onClick={submit} disabled={submitting} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="payment-submit-button">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {mode === "on_account" ? "Book on account"
                : isCOPD && paymentType === "down_payment" ? "Confirm DP"
                : isCOPD ? "Mark Payment Pending"
                : "Confirm payment"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProofUploader = ({ proofUrl, setProofUrl, uploadingProof, uploadProof, inputRef }) => (
  <div className="mt-1">
    {proofUrl ? (
      <div className="flex items-center gap-2 text-sm">
        <img src={fileUrl(proofUrl)} alt="" className="h-14 w-14 rounded object-cover" />
        <button type="button" onClick={() => setProofUrl(null)} data-testid="payment-proof-remove" className="text-red-600 hover:underline text-xs">
          <X className="h-3 w-3 inline" /> Remove
        </button>
      </div>
    ) : (
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploadingProof}
        data-testid="payment-proof-button"
        className="pav-input text-sm inline-flex items-center gap-2 hover:bg-[#F4F6FB]">
        {uploadingProof ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
        Upload proof
      </button>
    )}
    <input ref={inputRef} type="file" accept="image/*" className="hidden"
      data-testid="payment-proof-input"
      onChange={(e) => uploadProof(e.target.files?.[0])} />
  </div>
);

const OrderTable = ({ orders, onProcess, actionLabel, showPayment, unpaidFlag }) => (
  <div className="pav-card p-0 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
          <tr>
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Order</th>
            <th className="text-left px-3 py-2">Customer</th>
            <th className="text-left px-3 py-2">Items</th>
            {showPayment && <th className="text-left px-3 py-2">Payment</th>}
            <th className="text-right px-3 py-2">Total</th>
            {onProcess && <th className="text-right px-3 py-2">Action</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E2E8F0]">
          {orders.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-neutral-500">No orders.</td></tr>}
          {orders.map((o) => (
            <tr key={o.order_id} data-testid={`cashier-order-row-${o.order_id}`}>
              <td className="px-3 py-2 text-neutral-600">{new Date(o.created_at).toLocaleString()}</td>
              <td className="px-3 py-2 font-semibold">{o.order_id}</td>
              <td className="px-3 py-2">
                {o.customer_name}
                {o.walk_in && <span className="ml-2 text-[10px] uppercase text-[#06B6D4]">walk-in</span>}
              </td>
              <td className="px-3 py-2 text-xs text-neutral-600">{o.items?.[0]?.service_name || o.items?.[0]?.category || "—"} ×{o.items?.[0]?.quantity}</td>
              {showPayment && (
                <td className="px-3 py-2 capitalize">
                  {(o.payment_mode || "—").replace(/_/g, " ")}
                  {unpaidFlag && <span className="ml-2 text-red-600 text-[10px] uppercase">UNPAID</span>}
                </td>
              )}
              <td className="px-3 py-2 text-right font-bold">{peso(o.total)}</td>
              {onProcess && (
                <td className="px-3 py-2 text-right">
                  <button onClick={() => onProcess(o)} data-testid={`process-payment-${o.order_id}`}
                    className="btn-accent text-xs py-1.5 px-3">
                    {actionLabel}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default CashierDashboard;
